/**
 * useAiConfigStatus — shared hook for AI provider availability checks.
 *
 * Reuses the ['ai-configs', activeOrgId] TanStack Query key so that
 * AISettingsPanel, EmbeddingModelWidget, BackfillWidget, and
 * RorosSidebarLayout all share a single cache entry — no duplicate requests.
 *
 * AC-BYOK-6: hasEmbeddingCapableProvider → EmbeddingModelWidget + BackfillWidget
 * AC-BYOK-7: hasAnyEnabledProvider → AISettingsPanel warning banner
 * AC-BYOK-9: hasAnyEnabledProvider → RorosSidebarLayout nav indicator
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';

interface AIConfigCapabilities {
  text: boolean;
  vision: boolean;
  structuredOutput: boolean;
  embeddings: boolean;
  webSearch: boolean;
}

interface AIConfigRow {
  id: string;
  provider: string;
  enabled: boolean;
  capabilities: AIConfigCapabilities;
}

async function fetchAiConfigs(orgId: string): Promise<AIConfigRow[]> {
  const res = await apiFetch(`/api/orgs/${encodeURIComponent(orgId)}/ai-config`);
  if (!res.ok) return [];
  const data = await res.json();
  if (!data.success) return [];
  return data.data as AIConfigRow[];
}

export interface AiConfigStatus {
  /** True when at least one provider is configured AND enabled. */
  hasAnyEnabledProvider: boolean;
  /** True when at least one enabled provider has embeddings=true capability. */
  hasEmbeddingCapableProvider: boolean;
  isLoading: boolean;
}

export function useAiConfigStatus(): AiConfigStatus {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);

  const { data: configs = [], isLoading } = useQuery<AIConfigRow[]>({
    queryKey: ['ai-configs', activeOrgId],
    queryFn: () => fetchAiConfigs(activeOrgId!),
    enabled: !!activeOrgId,
  });

  const enabledConfigs = configs.filter((c) => c.enabled);
  const hasAnyEnabledProvider = enabledConfigs.length > 0;
  const hasEmbeddingCapableProvider = enabledConfigs.some((c) => c.capabilities?.embeddings);

  return { hasAnyEnabledProvider, hasEmbeddingCapableProvider, isLoading };
}
